import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import type { Pagina } from '../../types/gmud'
import type {
  DetalheDaOs,
  FiltrosDeOs,
  MeioFormal,
  MesSemOrdem,
  NovaOrdemDeServico,
  PrazoDaOs,
  ResumoDaOs,
  TipoDeFato,
} from '../../types/ordemDeServico'

/**
 * As ordens de serviço dos ANEXOS IA e IB.
 *
 * **A rota da API é `/ordens-de-servico`; o endereço da TELA é `/ordens-servico`.** Os dois estão
 * certos e não se copiam: o endereço da tela é o que `navegacaoContrato.ts` já publicou ao fiscal, e
 * a Regra nº 4 proíbe mexer em rota por causa de rótulo. Escrever um no lugar do outro dá 404 no
 * navegador ou no servidor, e nenhum teste de tipo pega.
 *
 * As chaves de cache ficam aqui, e não em `chaves.ts`, pelo mesmo motivo de `relatorios.ts`: aquele
 * arquivo é das GMUDs e do comitê, e cada área que cresce dentro dele empurra a próxima para o teto
 * de 200 linhas.
 */
const chaves = {
  /** Tudo de ordem de serviço. Invalidar isto derruba lista, detalhe, prazos e pendências. */
  tudo: () => ['ordens-de-servico'] as const,
  lista: (filtros: FiltrosDeOs) => ['ordens-de-servico', 'lista', filtros] as const,
  detalhe: (id: number) => ['ordens-de-servico', 'detalhe', id] as const,
  prazos: (id: number) => ['ordens-de-servico', 'prazos', id] as const,
  pendencias: () => ['ordens-de-servico', 'pendencias'] as const,
  tiposDeFato: () => ['ordens-de-servico', 'tipos-de-fato'] as const,
}

/** O servidor pagina em base zero; a barra de paginação da casa conta a partir de 1. */
function paraConsulta(filtros: FiltrosDeOs): Record<string, string | number> {
  const consulta: Record<string, string | number> = { pagina: Math.max(filtros.pagina - 1, 0), tamanho: 20 }
  if (filtros.competencia) {
    consulta.competencia = filtros.competencia
  }
  if (filtros.regime) {
    consulta.regime = filtros.regime
  }
  if (filtros.situacao) {
    consulta.situacao = filtros.situacao
  }
  return consulta
}

/**
 * A lista de ordens, com os filtros que estão na URL.
 *
 * @param filtros competência, regime, situação e página
 * @returns a consulta da página
 */
export function useListaDeOs(filtros: FiltrosDeOs) {
  return useQuery({
    queryKey: chaves.lista(filtros),
    queryFn: async () =>
      (await api.get<Pagina<ResumoDaOs>>('/ordens-de-servico', { params: paraConsulta(filtros) })).data,
    // Mantém a página anterior visível enquanto a próxima chega: sem isso a tabela pisca vazia, e
    // meio segundo de tabela vazia é lido como "não há ordem nenhuma".
    placeholderData: (anterior) => anterior,
  })
}

/**
 * O documento inteiro, com os itens já conferidos pelo servidor.
 *
 * @param id identificador da ordem
 * @returns a consulta do detalhe
 */
export function useOs(id: number) {
  return useQuery({
    queryKey: chaves.detalhe(id),
    queryFn: async () => (await api.get<DetalheDaOs>(`/ordens-de-servico/${id}`)).data,
    enabled: Number.isFinite(id),
  })
}

/**
 * Os prazos de recebimento da ordem.
 *
 * Prazo não apurável volta 200, com a razão por extenso — nunca erro. Por isso não há tratamento
 * especial aqui: a tela desenha a razão como desenha a data.
 *
 * @param id identificador da ordem
 * @returns a consulta dos prazos
 */
export function usePrazosDaOs(id: number) {
  return useQuery({
    queryKey: chaves.prazos(id),
    queryFn: async () => (await api.get<PrazoDaOs[]>(`/ordens-de-servico/${id}/prazos`)).data,
    enabled: Number.isFinite(id),
  })
}

/**
 * Os meses da vigência do contrato sem ordem registrada.
 *
 * **O endpoint é do lado CONTRATANTE** (`@PreAuthorize(CONTRATANTE)`), enquanto o resto da tela é
 * dos dois lados. Por isso o `habilitado`: sem ele, quem é da contratada abriria a lista e tomaria
 * um 403 no meio de uma tela que funciona — o que se lê como defeito do sistema, e não como limite
 * de acesso.
 *
 * @param habilitado se quem está vendo é do lado que pode consultar
 * @returns a consulta dos meses descobertos
 */
export function usePendenciasDeOs(habilitado: boolean) {
  return useQuery({
    queryKey: chaves.pendencias(),
    queryFn: async () => (await api.get<MesSemOrdem[]>('/ordens-de-servico/pendencias')).data,
    enabled: habilitado,
  })
}

/**
 * O catálogo de atos que podem ser registrados numa ordem.
 *
 * Vem do banco (`ref_fato_da_os`) e não de uma lista aqui: a CONTRATANTE pode criar documento por
 * ato administrativo (6.23, p.44), e uma cópia no navegador ficaria muda no mês em que isso valer.
 *
 * @returns a consulta do catálogo
 */
export function useTiposDeFato() {
  return useQuery({
    queryKey: chaves.tiposDeFato(),
    queryFn: async () => (await api.get<TipoDeFato[]>('/ordens-de-servico/tipos-de-fato')).data,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Registra a ordem inteira, num POST só.
 *
 * @returns a mutação do registro
 */
export function useRegistrarOs() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (ordem: NovaOrdemDeServico) =>
      (await api.post<DetalheDaOs>('/ordens-de-servico', ordem)).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}

/**
 * Revoga uma ordem de serviço, sem apagá-la.
 *
 * @returns a mutação da revogação
 */
export function useRevogarOs() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, motivo }: { id: number; motivo: string }) =>
      (await api.post<DetalheDaOs>(`/ordens-de-servico/${id}/revogar`, { motivo })).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}

/** O que a tela envia ao registrar um fato datado. */
export interface FatoParaRegistrar {
  id: number
  tipo: string
  ocorridoEm: string
  meio: MeioFormal | ''
  referencia: string
  observacao: string
}

/**
 * Registra um fato datado da ordem — é o registro que faz o prazo existir.
 *
 * O meio em branco vira `null` aqui, e não no componente: `""` não é valor do enum `MeioFormal` e
 * o servidor recusaria a requisição inteira com um 400 de desserialização, que não tem como virar
 * aviso de formulário. Informar o meio é opcional; mandar um meio inexistente não é.
 *
 * @returns a mutação do fato
 */
export function useRegistrarFato() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, meio, ...corpo }: FatoParaRegistrar) =>
      (await api.post(`/ordens-de-servico/${id}/fatos`, { ...corpo, meio: meio === '' ? null : meio })).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}

/**
 * Revoga um fato registrado por engano. Ele continua na tabela: dele corre prazo contratual.
 *
 * @returns a mutação da revogação do fato
 */
export function useRevogarFato() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, fatoId, motivo }: { id: number; fatoId: number; motivo: string }) =>
      (await api.post<DetalheDaOs>(`/ordens-de-servico/${id}/fatos/${fatoId}/revogar`, { motivo })).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}
